/**
 * QuickOfferSection.jsx
 * ======================
 * Section inline sur MandateDetailV2Page pour logger rapidement une offre reçue
 * (sans passer par la signature eIDAS). Alimente les KPIs offres.
 */
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import {
    Euro, Plus, CheckCircle2, XCircle, Clock3, TrendingDown, User,
} from 'lucide-react';

const STATUS_LABELS = {
    pending: { label: 'En attente', color: 'bg-amber-500/10 text-amber-300 border-amber-500/20', icon: Clock3 },
    accepted: { label: 'Acceptée', color: 'bg-green-500/10 text-green-300 border-green-500/20', icon: CheckCircle2 },
    rejected: { label: 'Refusée', color: 'bg-red-500/10 text-red-300 border-red-500/20', icon: XCircle },
    counter_offer: { label: 'Contre-offre', color: 'bg-blue-500/10 text-blue-300 border-blue-500/20', icon: TrendingDown },
};

function formatEUR(n) {
    if (n === null || n === undefined) return '—';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);
}

export default function QuickOfferSection({ mandateId, askingPrice }) {
    const [offers, setOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({
        offer_amount: '',
        buyer_name: '',
        buyer_phone: '',
        buyer_email: '',
        is_cash: false,
        received_at: new Date().toISOString().slice(0, 10),
        status: 'pending',
        had_counter_offer: false,
        notes: '',
    });

    const load = async () => {
        try {
            const { data } = await api.get(`/quick-offers/mandate/${mandateId}`);
            setOffers(data.offers || []);
        } catch (e) {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (mandateId) load();
    }, [mandateId]);

    const create = async () => {
        if (!form.offer_amount || !form.buyer_name) {
            toast.error('Montant et nom acquéreur requis');
            return;
        }
        try {
            await api.post('/quick-offers', {
                mandate_id: mandateId,
                offer_amount: Number(form.offer_amount),
                buyer_name: form.buyer_name,
                buyer_phone: form.buyer_phone || null,
                buyer_email: form.buyer_email || null,
                is_cash: form.is_cash,
                received_at: form.received_at ? new Date(form.received_at).toISOString() : null,
                status: form.status,
                had_counter_offer: form.had_counter_offer,
                notes: form.notes || null,
            });
            toast.success('Offre enregistrée');
            setShowForm(false);
            setForm({
                offer_amount: '', buyer_name: '', buyer_phone: '', buyer_email: '',
                is_cash: false, received_at: new Date().toISOString().slice(0, 10),
                status: 'pending', had_counter_offer: false, notes: '',
            });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur création');
        }
    };

    const updateStatus = async (offerId, newStatus) => {
        try {
            await api.patch(`/quick-offers/${offerId}`, { status: newStatus });
            toast.success('Statut mis à jour');

            // Si passage en "accepted", proposer de créer un shell compromis pour fermer la boucle
            if (newStatus === 'accepted') {
                const offer = offers.find(o => o.id === offerId);
                if (offer && window.confirm('Offre acceptée ! Créer un suivi compromis pré-rempli ?\n(Signé chez notaire + suivi acte → KPIs compromis)')) {
                    try {
                        await api.post('/compromis', {
                            mandate_id: mandateId,
                            offer_id: offerId,
                            signed_at: new Date().toISOString().slice(0, 10),
                            sale_amount: offer.offer_amount,
                            buyer_names: offer.buyers?.[0]
                                ? `${offer.buyers[0].first_name || ''} ${offer.buyers[0].last_name || ''}`.trim()
                                : '',
                            financing_status: 'pending',
                        });
                        toast.success('Shell compromis créé — allez le compléter dans la section Compromis');
                    } catch (e) {
                        // silent — user can still create manually
                    }
                }
            }
            load();
        } catch (e) {
            toast.error('Erreur mise à jour');
        }
    };

    const discount = form.offer_amount && askingPrice
        ? (((askingPrice - Number(form.offer_amount)) / askingPrice) * 100).toFixed(1)
        : null;

    return (
        <div className="space-y-3" data-testid="quick-offer-section">
            {loading ? (
                <p className="text-xs text-muted-foreground">Chargement…</p>
            ) : offers.length === 0 && !showForm ? (
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/5 text-center">
                    <Euro className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-3">Aucune offre enregistrée</p>
                    <Button
                        onClick={() => setShowForm(true)}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs"
                        data-testid="quick-offer-create-btn"
                    >
                        <Plus className="w-3.5 h-3.5 mr-1.5" />
                        Logger une offre reçue
                    </Button>
                </div>
            ) : (
                <>
                    {offers.map((o) => {
                        const s = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
                        const Icon = s.icon;
                        const buyer = (o.buyers || [{}])[0];
                        const disc = askingPrice && o.offer_amount
                            ? (((askingPrice - o.offer_amount) / askingPrice) * 100).toFixed(1)
                            : null;
                        return (
                            <div key={o.id} className="p-3 rounded-xl bg-white/[0.02] border border-white/10 space-y-2" data-testid={`offer-${o.id}`}>
                                <div className="flex items-start justify-between gap-2 flex-wrap">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${s.color}`}>
                                            <Icon className="w-3 h-3" />
                                            {s.label}
                                        </span>
                                        <span className="text-[10px] text-muted-foreground">
                                            {o.created_at ? new Date(o.created_at).toLocaleDateString('fr-FR') : '—'}
                                        </span>
                                    </div>
                                    <span className="text-sm font-bold text-foreground">{formatEUR(o.offer_amount)}</span>
                                </div>
                                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                        <User className="w-3 h-3" />
                                        {buyer.first_name} {buyer.last_name}
                                    </span>
                                    {disc !== null && (
                                        <span className={Number(disc) > 5 ? 'text-amber-400' : Number(disc) < 0 ? 'text-green-400' : ''}>
                                            {Number(disc) > 0 ? '-' : '+'}{Math.abs(Number(disc))}% vs affiché
                                        </span>
                                    )}
                                    {o.financing?.is_cash && <span className="text-green-400 font-bold">💰 Cash</span>}
                                </div>
                                {o.status === 'pending' && (
                                    <div className="flex items-center gap-2 pt-2 border-t border-white/5 flex-wrap">
                                        <Button size="sm" onClick={() => updateStatus(o.id, 'accepted')} className="text-[10px] h-7 bg-green-500/20 text-green-300 hover:bg-green-500/30" data-testid={`offer-accept-${o.id}`}>
                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Accepter
                                        </Button>
                                        <Button size="sm" onClick={() => updateStatus(o.id, 'counter_offer')} className="text-[10px] h-7 bg-blue-500/20 text-blue-300 hover:bg-blue-500/30">
                                            <TrendingDown className="w-3 h-3 mr-1" /> Contre
                                        </Button>
                                        <Button size="sm" onClick={() => updateStatus(o.id, 'rejected')} variant="ghost" className="text-[10px] h-7 text-red-300 hover:bg-red-500/10">
                                            <XCircle className="w-3 h-3 mr-1" /> Refuser
                                        </Button>
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {!showForm && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowForm(true)}
                            className="w-full text-xs"
                            data-testid="quick-offer-add-another-btn"
                        >
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Logger une autre offre
                        </Button>
                    )}
                </>
            )}

            <AnimatePresence>
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="p-4 rounded-xl bg-white/[0.03] border border-[#FF914D]/20 space-y-3"
                        data-testid="quick-offer-form"
                    >
                        <p className="text-[10px] uppercase tracking-wider font-bold text-[#FF914D]">Nouvelle offre reçue</p>
                        <div className="grid grid-cols-2 gap-2">
                            <Field label="Montant € *" type="number" value={form.offer_amount} onChange={(v) => setForm({ ...form, offer_amount: v })} testid="offer-amount" />
                            <Field label="Date réception" type="date" value={form.received_at} onChange={(v) => setForm({ ...form, received_at: v })} testid="offer-received-at" />
                            <Field label="Nom acquéreur *" value={form.buyer_name} onChange={(v) => setForm({ ...form, buyer_name: v })} testid="offer-buyer-name" />
                            <Field label="Téléphone" value={form.buyer_phone} onChange={(v) => setForm({ ...form, buyer_phone: v })} testid="offer-buyer-phone" />
                            <Field label="Email" type="email" value={form.buyer_email} onChange={(v) => setForm({ ...form, buyer_email: v })} testid="offer-buyer-email" />
                            <div>
                                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">Statut initial</p>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs"
                                    data-testid="offer-status-select"
                                >
                                    <option value="pending">En attente</option>
                                    <option value="accepted">Acceptée</option>
                                    <option value="rejected">Refusée</option>
                                    <option value="counter_offer">Contre-offre</option>
                                </select>
                            </div>
                        </div>
                        {discount !== null && askingPrice && (
                            <div className={`text-[11px] px-2 py-1 rounded ${Number(discount) > 5 ? 'bg-amber-500/10 text-amber-300' : 'bg-white/5 text-muted-foreground'}`}>
                                Écart : <b>{Number(discount) >= 0 ? '-' : '+'}{Math.abs(Number(discount))}%</b> vs prix affiché ({formatEUR(askingPrice)})
                            </div>
                        )}
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={form.is_cash} onChange={(e) => setForm({ ...form, is_cash: e.target.checked })} data-testid="offer-is-cash" />
                                <span className="text-muted-foreground">Cash / sans crédit</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={form.had_counter_offer} onChange={(e) => setForm({ ...form, had_counter_offer: e.target.checked })} data-testid="offer-had-counter" />
                                <span className="text-muted-foreground">Contre-offre faite par vendeur</span>
                            </label>
                        </div>
                        <textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Notes (optionnel)"
                            className="w-full px-2 py-1.5 bg-white/[0.03] border border-white/10 rounded text-foreground text-xs"
                            rows={2}
                        />
                        <div className="flex items-center gap-2 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} className="text-xs">Annuler</Button>
                            <Button size="sm" onClick={create} className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white text-xs" data-testid="quick-offer-save">
                                Enregistrer
                            </Button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function Field({ label, value, onChange, type = 'text', testid }) {
    return (
        <div>
            <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold mb-1">{label}</p>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-2 py-1.5 rounded bg-white/[0.03] border border-white/10 text-foreground text-xs focus:border-[#FF914D]/50 focus:outline-none"
                data-testid={testid}
            />
        </div>
    );
}
